import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, message, Empty, Alert, Switch, Input, Spin } from 'antd';
import { ReloadOutlined, PlayCircleOutlined, CheckCircleOutlined, SoundOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { filmApi } from '../../services/api/film';
import { apiErrorText } from './apiErrorText';
import { useFilmPlan } from './FilmPlanContext';
import './storyboard.css';
import type { FilmExportStatus, FilmExportMeta } from '../../types/film';

const STATUS: Record<FilmExportStatus, { pill: 'done' | 'warn' | 'no' | 'todo'; label: string }> = {
  pending: { pill: 'todo', label: 'Not assembled' },
  rendering: { pill: 'warn', label: 'Assembling…' },
  done: { pill: 'done', label: 'Assembled' },
  failed: { pill: 'no', label: 'Failed' },
};

/** E5 — Assembly & Export (HITL gate 3): dựng phim cuối, xem bản dựng, duyệt.
 *  eventReady (redesign event pipeline): sẵn-sàng tính theo clip đã chọn của event, không theo shot.videoUrl (model beat cũ). */
const FilmExportTab: React.FC<{ eventReady?: { done: number; total: number }; exportStale?: boolean | null }> = ({ eventReady, exportStale }) => {
  const { plan, planId, reloadPlans } = useFilmPlan();
  const [exp, setExp] = useState<{ exportUrl: string | null; exportStatus: FilmExportStatus | null; exportMeta: FilmExportMeta | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [bgmUrl, setBgmUrl] = useState('');
  const timer = useRef<any>(null);

  const allShots = (plan?.scenes || []).flatMap((s) => s.shots);
  const withVideo = allShots.filter((s) => s.videoUrl).length;
  const ready = eventReady
    ? eventReady.total > 0 && eventReady.done >= eventReady.total
    : allShots.length > 0 && withVideo === allShots.length;
  const readyMsg = eventReady
    ? `Not enough clips: ${eventReady.done}/${eventReady.total} events have a selected clip`
    : `Not enough clips: ${withVideo}/${allShots.length} shots have video`;

  const fetchExport = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    try { setExp(await filmApi.getExport(planId)); }
    catch (e: any) { message.error(`Failed to load export: ${e?.response?.data?.message || e?.message}`); }
    finally { setLoading(false); }
  }, [planId]);

  useEffect(() => { setExp(null); fetchExport(); }, [fetchExport]);

  // Poll khi đang render.
  useEffect(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (exp?.exportStatus === 'rendering' && planId) timer.current = setInterval(fetchExport, 15000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [exp?.exportStatus, planId, fetchExport]);

  const assemble = async () => {
    if (!planId) return;
    setBusy(true);
    try {
      const r = await filmApi.assemblePlan(planId, { force, bgmUrl: bgmUrl.trim() || undefined });
      message.success(r.message || 'Started assembling film in the background');
      setExp((p) => ({ exportUrl: p?.exportUrl ?? null, exportStatus: 'rendering', exportMeta: p?.exportMeta ?? null }));
    } catch (e: any) { message.error(apiErrorText(e, 'Ghép phim')); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    if (!planId) return;
    try {
      await filmApi.updateStatus(planId, 'ASSEMBLED');
      message.success('Assembly approved (ASSEMBLED)');
      reloadPlans();
    } catch (e: any) { message.error(`Error: ${e?.response?.data?.message || e?.message}`); }
  };

  if (!plan) return <div className="sb-scope" style={{ padding: 24 }}><Empty description="Select a plan first" /></div>;

  const st = exp?.exportStatus || 'pending';
  const meta = exp?.exportMeta;
  const rendering = st === 'rendering';
  const clipsLabel = eventReady ? `${eventReady.done}/${eventReady.total} events` : `${withVideo}/${allShots.length} shots`;

  return (
    <div className="sb-scope">
      <div className="sb-ref-head" style={{ marginBottom: 14 }}>
        <h3>Assemble film → MP4</h3>
        <span className={`sb-state ${STATUS[st].pill}`}>{STATUS[st].label}</span>
        <span style={{ marginLeft: 'auto' }} />
        {rendering && <Spin size="small" />}
        <Button icon={<ReloadOutlined />} size="small" onClick={fetchExport} loading={loading} />
      </div>

      {exportStale && exp?.exportUrl && <Alert style={{ marginBottom: 14, maxWidth: 900 }} type="warning" showIcon message="Film is out of date" description="You edited events or regenerated clips after the last assembly. Re-assemble to refresh the MP4." />}
      {!ready && <Alert style={{ marginBottom: 14, maxWidth: 900 }} type="warning" showIcon message={readyMsg}
        description="You should generate and select a clip for every event before stitching. You can still stitch what's available." />}
      {st === 'failed' && meta?.error && <Alert style={{ marginBottom: 14, maxWidth: 900 }} type="error" showIcon message="Assembly failed" description={meta.error} />}
      {rendering && <Alert style={{ marginBottom: 14, maxWidth: 900 }} type="info" showIcon
        message="Assembling in the background (subtitles from audio + Remotion render)"
        description="Auto-refreshes every 15s. A 60–90s short usually takes a few minutes." />}

      <div className="sb-asm">
        <div>
          <div className="prev">
            {exp?.exportUrl
              ? <video src={exp.exportUrl} controls preload="metadata" />
              : <div className="pc">
                  {rendering ? <><Spin /><span className="hint">rendering…</span></> : <><VideoCameraOutlined style={{ fontSize: 30, color: 'rgba(255,255,255,.5)' }} /><span className="hint">no film yet</span></>}
                </div>}
          </div>
          {exp?.exportUrl && <Button href={exp.exportUrl} target="_blank" size="small" block style={{ marginTop: 8 }}>Open in new tab</Button>}
        </div>

        <div>
          <h3>Final render</h3>
          <p className="lead">Stitch the selected clip of every event, add timed subtitles and music, render a vertical 9:16 master.</p>

          <div className="sb-kv">
            <span className="k">Duration</span><span className="v">{meta?.durationSec != null ? `${meta.durationSec}s` : '—'}</span>
            <span className="k">Clips</span><span className="v">{meta?.clipCount != null ? meta.clipCount : clipsLabel}</span>
            <span className="k">Subtitled shots</span><span className="v">{meta?.subtitledShots ?? '—'}</span>
            <span className="k">Aspect</span><span className="v">1080 × 1920 · 9:16</span>
            {meta?.renderMs != null && <><span className="k">Render</span><span className="v">{(meta.renderMs / 1000).toFixed(0)}s</span></>}
          </div>

          <div className="sb-opt">
            <Switch size="small" checked={force} onChange={setForce} disabled={rendering} />
            <div><div className="ol">Regenerate subtitles</div><div className="od">Rebuild timed captions from the audio</div></div>
          </div>
          <div className="sb-opt">
            <Input prefix={<SoundOutlined />} placeholder="Paste a track URL…" value={bgmUrl} onChange={(e) => setBgmUrl(e.target.value)} disabled={rendering} allowClear size="small" style={{ maxWidth: 320 }} />
            <div><div className="ol">Background music</div><div className="od">Optional — a music bed under the film</div></div>
          </div>

          <div className="sb-asm-act">
            <Button type="primary" icon={<PlayCircleOutlined />} loading={busy || rendering} onClick={assemble}>{exp?.exportUrl ? 'Re-assemble' : 'Assemble film'}</Button>
            {exp?.exportUrl && <Button type="primary" ghost icon={<CheckCircleOutlined />} disabled={st !== 'done' || plan.status === 'ASSEMBLED'} onClick={approve}>{plan.status === 'ASSEMBLED' ? 'Approved' : 'Approve'}</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilmExportTab;
